import { useEffect } from 'react'
import { Editor, TLShapeId } from '@tldraw/tldraw'

/**
 * Hook to handle keyboard shortcuts for the board.
 * Handles both tool selection (v/e/x/p/b/d) and shape deletion (Delete/Backspace).
 *
 * @param editor - The tldraw editor instance
 * @param onToolChange - Optional callback to run before changing tools (e.g., exit comment mode)
 *
 * Keyboard shortcuts:
 * - v: Select tool
 * - e: Eraser tool
 * - x/d: Draw tool
 * - p: Hand/pan tool
 * - b: Arrow tool
 * - Delete/Backspace: Delete selected shapes
 */
export function useKeyboardShortcuts(editor: Editor | null, onToolChange?: () => void, onZoomToggle?: () => void) {
  useEffect(() => {
    if (!editor) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not focused on an input, textarea, or contenteditable element
      const active = document.activeElement
      const isTextInput =
        active &&
        ((active.tagName === 'INPUT' &&
          (active as HTMLInputElement).type === 'text') ||
          active.tagName === 'TEXTAREA' ||
          (active as HTMLElement).isContentEditable)
      if (isTextInput) return

      const isReadonly = editor.getInstanceState().isReadonly

      // Handle deletion - filter out CommentPin shapes to prevent orphaned pins
      // CommentPin shapes should only be deleted through the comment actions (popover or context menu)
      // which properly delete the backend comment and let the sync effect clean up the pin
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isReadonly) {
        const selected = editor.getSelectedShapeIds()
        const deletable = selected.filter((id: TLShapeId) => {
          const shape = editor.getShape(id)
          return shape?.type !== 'CommentPin'
        })
        if (deletable.length > 0) {
          editor.deleteShapes(deletable)
          e.preventDefault()
        }
        return
      }

      // Handle tool selection shortcuts (only when not locked)
      if (!isReadonly) {
        switch (e.key) {
          case 'v':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('select')
            e.preventDefault()
            break
          case 'e':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('eraser')
            e.preventDefault()
            break
          case 'x':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('draw')
            e.preventDefault()
            break
          case 'p':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('hand')
            e.preventDefault()
            break
          case 'b':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('arrow')
            e.preventDefault()
            break
          case 'd':
            onToolChange?.() // Exit comment mode before switching tools
            editor.setCurrentTool('draw')
            e.preventDefault()
            break
          case 'z':
            onZoomToggle?.()
            e.preventDefault()
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [editor, onToolChange, onZoomToggle])
}
